import { Component, ElementRef, OnInit, ViewChild, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LeftBarComponent } from '@app/shared/results/left-bar/left-bar.component';
import { SkillprintComponent } from './skillprint/skillprint.component';
import { PlanPathComponent } from './plan-path/plan-path.component';
import { TakeActionComponent } from './take-action/take-action.component';
import { BYP_PLAN_GENERATED_LOCAL_KEY } from '@app/utils/constants';
import { GeneratePlanComponent } from './generate-plan/generate-plan.component';
import { CareerChecklistStore } from '@app/services/signals/career-checklist-service';
import { stringToBoolean } from '@app/utils/sharedFunctions';
import { analytics } from '@app/events/segment/analytics';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SuccessSnackBarComponent } from '@app/shared/snack-bars/success/success.component';
import { SuccessDefaultOptions } from '@app/shared/snack-bars/success/success.utils';

@Component({
  selector: 'app-build-plan',
  imports: [
    CommonModule, 
    LeftBarComponent,
    SkillprintComponent,
    PlanPathComponent,
    TakeActionComponent,
    GeneratePlanComponent,
  ],
  templateUrl: './build-plan.component.html',
  styleUrl: './build-plan.component.css'
})
export class BuildPlanComponent implements OnInit {
  @ViewChild('contentDiv') contentDiv!: ElementRef<HTMLDivElement>;
  @ViewChild('skillprint') skillprintComponent!: SkillprintComponent;
  @ViewChild('planPath') planPathComponent!: PlanPathComponent;
  private scrollTimeout: any;
  activeStep: number = 1;
  public isPlanGenerated: boolean = false;

  private readonly eventNames = {
    1: 'Plan Navigate Strength',
    2: 'Plan Navigate Path',
    3: 'Plan Navigate Action'
  };

  constructor(
    private checklistStore: CareerChecklistStore,
    private snackBar: MatSnackBar,
  ) {
    effect(() => {
      const planGenerated = this.checklistStore.buildPlanGenerated();
      if (planGenerated) {
        this.isPlanGenerated = true;
      }
    });
  }

  ngOnInit() {
    // Check localStorage first for initial state
    const localStorageValue = stringToBoolean(localStorage.getItem(BYP_PLAN_GENERATED_LOCAL_KEY));
    
    // Also check the checklist store (which gets updated by left-bar after API call)
    const storeValue = this.checklistStore.buildPlanGenerated();
    
    // Use either value if true
    this.isPlanGenerated = localStorageValue || storeValue;
  }

  onScroll() {
    const el = this.contentDiv.nativeElement;
    el.classList.add('show-scrollbar');

    // Add sticky class to steps-div when scrolled
    const stepsDiv = el.querySelector('.steps-div') as HTMLElement;
    if (stepsDiv) {
      const scrollTop = el.scrollTop;
      if (scrollTop > 100) {
        stepsDiv.classList.add('is-sticky');
      } else {
        stepsDiv.classList.remove('is-sticky');
      }
    }

    // Detect which section is in view and update active step
    this.updateActiveStep();

    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      el.classList.remove('show-scrollbar');
    }, 1000);
  }

  private updateActiveStep() {
    const scrollContainer = this.contentDiv.nativeElement;
    const scrollTop = scrollContainer.scrollTop;
    const stepsDiv = scrollContainer.querySelector('.steps-div') as HTMLElement;
    const stickyHeaderHeight = stepsDiv ? stepsDiv.offsetHeight : 0;

    // Get all section elements
    const skillprintElement = scrollContainer.querySelector('app-skillprint') as HTMLElement;
    const planPathElement = scrollContainer.querySelector('app-plan-path') as HTMLElement;
    const takeActionElement = scrollContainer.querySelector('app-take-action') as HTMLElement;

    if (!skillprintElement || !planPathElement || !takeActionElement) {
      return;
    }

    // Calculate positions relative to scroll container with offset for sticky header
    const offset = stickyHeaderHeight + 100; // Add some buffer
    const skillprintTop = skillprintElement.offsetTop - offset;
    const planPathTop = planPathElement.offsetTop - offset;
    const takeActionTop = takeActionElement.offsetTop - offset;

    // Determine which section is in view
    if (scrollTop >= takeActionTop) {
      this.activeStep = 3;
    } else if (scrollTop >= planPathTop) {
      this.activeStep = 2;
    } else {
      this.activeStep = 1;
    }
  }

  scrollToStep(stepNumber: number) {
    if (this.isPlanGenerated) {
      analytics.track(this.eventNames[stepNumber as keyof typeof this.eventNames], { section: 'plan' });
      const scrollContainer = this.contentDiv.nativeElement;
      let targetElement: HTMLElement | null = null;

      // Immediately update active step for instant feedback
      this.activeStep = stepNumber;

      switch (stepNumber) {
        case 1:
          // Find the skillprint component's host element
          targetElement = scrollContainer.querySelector('app-skillprint');
          break;
        case 2:
          // Find the plan-path component's host element
          targetElement = scrollContainer.querySelector('app-plan-path');
          break;
        case 3:
          // Find the take-action component's host element
          targetElement = scrollContainer.querySelector('app-take-action');
          break;
      }

      if (targetElement) {
        // Get the sticky header height
        const stepsDiv = scrollContainer.querySelector('.steps-div') as HTMLElement;
        const stickyHeaderHeight = stepsDiv ? stepsDiv.offsetHeight : 0;

        // Calculate the target position relative to the scroll container
        const targetPosition = targetElement.offsetTop - stickyHeaderHeight - 10;

        // Smooth scroll to the calculated position
        scrollContainer.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      }
    }
  }

  onClickPlanGenerate() {
    localStorage.setItem(BYP_PLAN_GENERATED_LOCAL_KEY, "true");
    this.isPlanGenerated = true;
    this.checklistStore.setBuildPlanGenerate();
  }

  onResumeUploaded() {
    this.skillprintComponent.loadSkillprintSummary();
    this.planPathComponent.loadPlanPath();
    this.snackBar.openFromComponent(SuccessSnackBarComponent, {
      ...SuccessDefaultOptions,
      duration: 5000,
      data: { message: 'Your resume has been updated. Your plan is being refreshed.' },
    });
  }
}